'use client';

import classNames from 'classnames';
import { type ReactNode } from 'react';

import { usePathname } from '../../i18n';
import { type LocalPaths } from '../../utils/data/get-local-paths';
import { handleGTMHeaderClickEvent, hasCurrentRouteSubMenu } from '../../utils/helpers';
import { type LanguagesContent } from '../../utils/hooks/get-languages-content';
import { type NavigationItem } from '../../utils/types/navigation-items';
import { ContentContainer } from '../content-container';

// eslint-disable-next-line import/no-cycle
import { HeaderContent } from './components/header-content/header-content';
import { HeaderContentMobile } from './components/header-content-mobile/header-content-mobile';
import { LoaderStripContainer } from './components/loader-strip-container';
import { type HeaderAccountDropdownContent } from './graphql';
import { useIsHeaderSecondaryTheme } from './utils/hooks';
import styles from './header.module.scss';

type HeaderClientProps = {
  headerContentProps: {
    homeLabel: string;
    isAuthenticated: boolean;
    languagesCmsContent: LanguagesContent;
    profileDropdownCmsContent: HeaderAccountDropdownContent;
    profileDropdownSlot: ReactNode;
    profileDropdownSummarySlot: ReactNode;
    statusChipSlot: ReactNode;
  };
  navigationItems: NavigationItem[];
  paths: LocalPaths;
};

export const HeaderClient = ({ headerContentProps, navigationItems, paths }: HeaderClientProps) => {
  const pathname = usePathname();
  const hasSubMenu = hasCurrentRouteSubMenu(pathname, navigationItems);

  const isSecondaryTheme = useIsHeaderSecondaryTheme({ paths });

  return (
    <div
      className={classNames(styles['header-wrapper'], {
        [styles['header-wrapper--secondary']]: isSecondaryTheme,
        [styles['header-wrapper--has-sub-menu']]: hasSubMenu,
      })}
    >
      <ContentContainer>
        <header
          className={styles.header}
          id="header"
          onAuxClick={handleGTMHeaderClickEvent}
          onClick={handleGTMHeaderClickEvent}
        >
          <HeaderContentMobile
            isSecondaryTheme={isSecondaryTheme}
            navigationItems={navigationItems}
            {...headerContentProps}
          />
          <HeaderContent
            isSecondaryTheme={isSecondaryTheme}
            navigationItems={navigationItems}
            {...headerContentProps}
          />
        </header>
      </ContentContainer>
      <div className={styles['loader-container']}>
        <LoaderStripContainer />
      </div>
    </div>
  );
};
